import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import axios from "axios";
import {
  Truck, Search, X, Save, Trash2, Users, Receipt,
  ArrowLeft, Share2, Printer, Download, ChevronLeft, CheckSquare, Square, ListChecks,
  Check, Banknote, Store, MapPin, Phone, Hash, Package, UserPlus, Loader2, Eye,
} from "lucide-react";
import InvoiceModal from "../InvoiceModal";
import { buildBulkPrintHtml } from "./BulkConfirmPrintButton";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import { printHTML } from "../../utils/printHTML";
import { baghdadToday, baghdadDaysAgo, baghdadFirstOfMonth } from "../../utils/baghdadTime";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar-IQ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const C = {
  blue: "#2563EB", darkBlue: "#1E3A5F", green: "#059669",
  orange: "#F97316", red: "#DC2626", purple: "#7C3AED",
};

const DriverBundlesTab = () => {
  const [view, setView] = useState("create"); // "create" | "list" | "report"
  const [detail, setDetail] = useState(null);

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${C.darkBlue}, ${C.purple})` }}>
        <div className="p-4 text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Truck className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-black text-lg">الفواتير مع السواق</h2>
            <p className="text-[11px] opacity-80">حزم توزيع + تسديدات فورية + تقرير حصيلة</p>
          </div>
        </div>
        {!detail && (
          <div className="p-2 bg-white flex gap-1 flex-wrap">
            <button type="button" onClick={() => setView("create")}
              data-testid="bundle-tab-create"
              className={`flex-1 min-w-[80px] py-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 transition-all ${view === "create" ? "text-white shadow-md" : "text-slate-600"}`}
              style={view === "create" ? { background: C.purple } : {}}>
              <ListChecks className="w-4 h-4" /> إنشاء
            </button>
            <button type="button" onClick={() => setView("list")}
              data-testid="bundle-tab-list"
              className={`flex-1 min-w-[80px] py-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 transition-all ${view === "list" ? "text-white shadow-md" : "text-slate-600"}`}
              style={view === "list" ? { background: C.purple } : {}}>
              <Users className="w-4 h-4" /> الحزم
            </button>
            <button type="button" onClick={() => setView("materials")}
              data-testid="bundle-tab-materials"
              className={`flex-1 min-w-[80px] py-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 transition-all ${view === "materials" ? "text-white shadow-md" : "text-slate-600"}`}
              style={view === "materials" ? { background: C.blue } : {}}>
              <Package className="w-4 h-4" /> المواد
            </button>
            <button type="button" onClick={() => setView("report")}
              data-testid="bundle-tab-report"
              className={`flex-1 min-w-[80px] py-2 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 transition-all ${view === "report" ? "text-white shadow-md" : "text-slate-600"}`}
              style={view === "report" ? { background: C.green } : {}}>
              <Receipt className="w-4 h-4" /> الحصيلة
            </button>
          </div>
        )}
      </div>

      {detail ? (
        <BundleDetail bundle={detail} onBack={() => setDetail(null)} onDeleted={() => setDetail(null)} />
      ) : view === "create" ? (
        <CreateBundle onCreated={() => { setView("list"); }} />
      ) : view === "list" ? (
        <BundleList onOpen={(b) => setDetail(b)} />
      ) : view === "materials" ? (
        <BundleMaterialsPicker />
      ) : (
        <CollectionsReport />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Create Bundle
// ═══════════════════════════════════════════════════════════════════════
const CreateBundle = ({ onCreated }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const deferred = useDeferredValue(search);
  const [selected, setSelected] = useState(new Set());
  const [driverName, setDriverName] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  // Feature Feb 2026: bulk-hide invoices from the picker so today's
  // list stays clean. `showHidden` toggles between visible/hidden pools.
  const [showHidden, setShowHidden] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [printing, setPrinting] = useState(false);

  const fetchFull = async (ids) => {
    const { data } = await axios.post(`${API}/admin/orders/for-print`, { order_ids: ids });
    return data.orders || [];
  };
  const openView = async (id) => {
    try {
      const [o] = await fetchFull([id]);
      if (o) setViewOrder(o);
    } catch { toast.error("فشل فتح الفاتورة"); }
  };
  const printAll = async () => {
    const ids = selected.size > 0 ? Array.from(selected) : filtered.map((o) => o.id);
    if (ids.length === 0) { toast.error("ما اكو فواتير للطباعة"); return; }
    setPrinting(true);
    try {
      const full = await fetchFull(ids);
      const order = new Map(ids.map((id, i) => [id, i]));
      full.sort((a, b) => order.get(a.id) - order.get(b.id));
      printHTML(buildBulkPrintHtml(full), { title: "طباعة الفواتير" });
      toast.success(`تم إرسال ${full.length} فاتورة للطابعة`);
    } catch (e) { toast.error(e.response?.data?.detail || "فشلت الطباعة"); }
    finally { setPrinting(false); }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/confirmable-orders`, {
        params: { include_hidden: showHidden ? "true" : "false" },
      });
      // When viewing hidden, keep only bundle_hidden=true; when viewing
      // visible, the endpoint already excluded them.
      const list = data.orders || [];
      setOrders(showHidden ? list.filter((o) => o.bundle_hidden) : list);
    } catch { toast.error("فشل تحميل الفواتير"); }
    finally { setLoading(false); }
  };
  const fetchDrivers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/drivers`);
      setDrivers(Array.isArray(data) ? data : (data.drivers || []));
    } catch { /* silent — freeform typing still works */ }
  };
  useEffect(() => { fetchOrders(); fetchDrivers(); /* eslint-disable-next-line */ }, [showHidden]);

  const filtered = useMemo(() => {
    if (!deferred.trim()) return orders;
    return orders.filter((o) => smartMatch(`${o.user_name || ""} ${o.user_phone || ""} ${o.invoice_number || ""}`, deferred));
  }, [orders, deferred]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((o) => o.id)));
  };

  const selectedOrders = orders.filter((o) => selected.has(o.id));
  const sumTotal = selectedOrders.reduce((s, o) => s + Number(o.total || 0), 0);
  const sumPrev = selectedOrders.reduce((s, o) => s + Number(o.previous_debt || 0), 0);

  const submit = async () => {
    if (!driverName.trim()) { toast.error("اكتب اسم السائق"); return; }
    if (selected.size === 0) { toast.error("اختر فاتورة واحدة على الأقل"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/driver-bundles`, {
        driver_name: driverName.trim(),
        order_ids: Array.from(selected),
      });
      toast.success(`تمّ إنشاء حزمة (${selected.size}) فاتورة للسائق «${driverName}»`);
      setSelected(new Set());
      setDriverName("");
      fetchOrders();
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {/* Driver + search */}
      <div className="bg-white rounded-2xl border-2 border-purple-100 p-3 space-y-2">
        <div>
          <label className="text-[10px] font-bold text-slate-500 block mb-1">🚚 اسم السائق</label>
          <input
            type="text"
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
            list="bundle-drivers-list"
            placeholder="اكتب اسم السائق..."
            data-testid="bundle-driver-name"
            className="w-full px-3 py-2.5 rounded-xl border-2 border-purple-200 focus:outline-none focus:border-purple-500 text-sm font-bold"
          />
          <datalist id="bundle-drivers-list">
            {drivers.map((d) => <option key={d.id} value={d.full_name || d.name || d.phone} />)}
          </datalist>
        </div>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث عن زبون بالاسم أو الهاتف..."
            data-testid="bundle-search"
            className="w-full pr-9 pl-3 py-2 rounded-xl border-2 border-purple-100 focus:outline-none focus:border-purple-400 text-sm" />
        </div>
      </div>

      {/* Selection summary */}
      {selected.size > 0 && (
        <div className="rounded-2xl p-3 grid grid-cols-3 gap-2" style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.blue})` }}>
          <div className="text-white text-center">
            <p className="text-[10px] opacity-80">مختارة</p>
            <p className="font-black text-lg">{selected.size}</p>
          </div>
          <div className="text-white text-center">
            <p className="text-[10px] opacity-80">إجمالي فواتير</p>
            <p className="font-black text-lg" dir="ltr">{fmt(sumTotal)}</p>
          </div>
          <div className="text-white text-center">
            <p className="text-[10px] opacity-80">مديونية سابقة</p>
            <p className="font-black text-lg" dir="ltr">{fmt(sumPrev)}</p>
          </div>
        </div>
      )}

      {/* Table of orders */}
      <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2 flex-wrap">
          <button type="button" onClick={toggleAll}
            data-testid="bundle-select-all"
            className="flex items-center gap-1 text-xs font-bold text-purple-700 hover:bg-purple-50 px-2 py-1 rounded-full">
            {selected.size === filtered.length && filtered.length > 0
              ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {selected.size === filtered.length && filtered.length > 0 ? "إلغاء الكل" : "تحديد الكل"}
          </button>
          <span className="text-[11px] text-slate-500">({filtered.length} {showHidden ? "مخفية" : "متاحة"})</span>
          <div className="mr-auto flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={printAll}
              disabled={printing || filtered.length === 0}
              data-testid="bundle-print-all"
              className="text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 text-white shadow-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition"
            >
              {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              {selected.size > 0 ? `طباعة المحدد (${selected.size})` : `طباعة الكل (${filtered.length})`}
            </button>
            {/* Bulk hide / un-hide button */}
            {selected.size > 0 && (
              <button
                type="button"
                onClick={async () => {
                  const ids = Array.from(selected);
                  const endpoint = showHidden ? "bulk-unhide" : "bulk-hide";
                  const verb = showHidden ? "إظهار" : "إخفاء";
                  try {
                    await axios.post(`${API}/admin/confirmable-orders/${endpoint}`, { order_ids: ids });
                    toast.success(`تم ${verb} ${ids.length} فاتورة`);
                    setSelected(new Set());
                    fetchOrders();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || `فشل ${verb}`);
                  }
                }}
                data-testid="bundle-bulk-hide"
                className="text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 text-white shadow-md"
                style={{ background: showHidden ? "#3b82f6" : "#f59e0b" }}
              >
                {showHidden ? "👁️ إظهار" : "🙈 إخفاء"} ({selected.size})
              </button>
            )}
            {/* One-click: restore ALL hidden confirmable orders. Big
                amber button so admins who accidentally bulk-hid a batch
                (or found their fresh-confirmed invoices missing) can
                bring them all back with one tap. */}
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("راح ترجع كل الفواتير المخفية اللي بلا حزمة إلى قائمة الإنشاء. نكمل؟")) return;
                try {
                  const { data } = await axios.post(`${API}/admin/confirmable-orders/unhide-all`);
                  const n = data?.unhidden || 0;
                  if (n > 0) {
                    toast.success(`تم استرجاع ${n} فاتورة إلى الإنشاء`);
                  } else {
                    toast.info("ما اكو فواتير مخفية للاسترجاع");
                  }
                  setShowHidden(false);
                  setSelected(new Set());
                  fetchOrders();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "فشل الاسترجاع");
                }
              }}
              data-testid="bundle-restore-all-hidden"
              className="text-[11px] font-bold px-3 py-1.5 rounded-full text-white shadow-md bg-emerald-600 hover:bg-emerald-700 transition"
              title="استرجاع كل الفواتير المخفية إلى قائمة الإنشاء بضغطة واحدة"
            >
              ⤴️ استرجاع الكل
            </button>
            {/* Toggle between visible & hidden pools */}
            <button
              type="button"
              onClick={() => { setShowHidden(!showHidden); setSelected(new Set()); }}
              data-testid="bundle-toggle-hidden"
              className={`text-[11px] font-bold px-3 py-1.5 rounded-full border-2 transition-all ${
                showHidden
                  ? "bg-purple-100 text-purple-700 border-purple-300"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              title={showHidden ? "عرض الفواتير المتاحة" : "عرض الفواتير المخفية"}
            >
              {showHidden ? "🔙 المتاحة" : "🗂️ المخفية"}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-8 text-slate-500">جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">
            {orders.length === 0 ? "لا توجد فواتير مؤكدة قابلة للحزم" : "لا نتائج"}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-purple-50 text-purple-700 sticky top-0">
                <tr>
                  <th className="p-2 text-right w-8"></th>
                  <th className="p-2 text-right">الزبون</th>
                  <th className="p-2 text-center">فاتورة</th>
                  <th className="p-2 text-center">مديونية سابقة</th>
                  <th className="p-2 text-center w-10">عرض</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const isSel = selected.has(o.id);
                  return (
                    <tr key={o.id}
                      onClick={() => toggle(o.id)}
                      data-testid={`bundle-row-${o.id}`}
                      className={`border-t border-slate-100 cursor-pointer transition-colors ${isSel ? "bg-purple-50" : "hover:bg-slate-50"}`}>
                      <td className="p-2 text-center">
                        {isSel
                          ? <CheckSquare className="w-4 h-4 mx-auto" style={{ color: C.purple }} />
                          : <Square className="w-4 h-4 mx-auto text-slate-300" />}
                      </td>
                      <td className="p-2">
                        <p className="font-bold text-slate-800">{o.user_name || "—"}</p>
                        {o.market_name && <p className="text-[10px] font-bold" style={{ color: C.blue }}>🏪 {o.market_name}</p>}
                        {o.address && <p className="text-[10px] text-slate-600 truncate">📍 {o.address}</p>}
                        {o.user_phone && <p className="text-[10px] text-slate-500" dir="ltr">{o.user_phone}</p>}
                        {o.invoice_number && <p className="text-[10px] text-blue-600" dir="ltr">#{o.invoice_number}</p>}
                      </td>
                      <td className="p-2 text-center font-black" dir="ltr" style={{ color: C.green }}>
                        {fmt(o.total)}
                      </td>
                      <td className="p-2 text-center font-black" dir="ltr" style={{ color: o.previous_debt > 0 ? C.red : "#94a3b8" }}>
                        {fmt(o.previous_debt || 0)}
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); openView(o.id); }}
                          data-testid={`bundle-view-invoice-${o.id}`}
                          title="عرض الفاتورة"
                          className="w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center mx-auto transition"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <button type="button" onClick={submit} disabled={busy || selected.size === 0 || !driverName.trim()}
        data-testid="bundle-submit"
        className="w-full py-3 rounded-full text-white font-black text-base shadow-lg disabled:opacity-40 flex items-center justify-center gap-2"
        style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.blue})` }}>
        <Save className="w-5 h-5" />
        إنشاء الحزمة ({selected.size} فاتورة)
      </button>
      {viewOrder && (
        <InvoiceModal
          order={viewOrder}
          customerInfo={{
            full_name: viewOrder.customer_name, phone: viewOrder.customer_phone,
            market_name: viewOrder.customer_market_name, customer_debt: viewOrder.customer_debt,
          }}
          onClose={() => setViewOrder(null)}
          viewOnly={true}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Bundle List
// ═══════════════════════════════════════════════════════════════════════
const BundleList = ({ onOpen }) => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const deferred = useDeferredValue(q);
  // Feb 2026: date-range filter so old bundles are reachable. Default =
  // 7 days back → today. Baghdad-TZ so bundles created in the first 3 h
  // of a Baghdad day (21:00–24:00 UTC) still land inside "today" — the
  // deployer RCA of Sept 19 pinpointed a UTC/Baghdad mismatch here.
  const today = baghdadToday();
  const weekAgo = baghdadDaysAgo(7);
  const [dateFrom, setDateFrom] = useState(weekAgo);
  const [dateTo, setDateTo] = useState(today);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/driver-bundles`, {
        params: { q: deferred, date_from: dateFrom, date_to: dateTo },
      });
      setBundles(data.bundles || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [deferred, dateFrom, dateTo]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث باسم السائق..."
          data-testid="bundles-search"
          className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-purple-100 focus:outline-none focus:border-purple-400 text-sm text-slate-900" />
      </div>

      {/* Date-range filter */}
      <div className="bg-white rounded-2xl p-3 border-2 border-purple-100 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[140px] flex items-center gap-1">
          <label className="text-[10px] font-bold text-slate-600 shrink-0">📅 من</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="bundles-date-from" max={today}
            className="flex-1 px-2 py-2 rounded-xl border-2 border-purple-100 focus:border-purple-400 text-xs font-bold focus:outline-none bg-white text-slate-900" dir="ltr" />
        </div>
        <div className="flex-1 min-w-[140px] flex items-center gap-1">
          <label className="text-[10px] font-bold text-slate-600 shrink-0">إلى</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="bundles-date-to" max={today}
            className="flex-1 px-2 py-2 rounded-xl border-2 border-purple-100 focus:border-purple-400 text-xs font-bold focus:outline-none bg-white text-slate-900" dir="ltr" />
        </div>
        <button
          onClick={() => { setDateFrom(today); setDateTo(today); }}
          className="text-[10px] font-bold px-3 py-2 rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-700"
        >
          اليوم
        </button>
        <button
          onClick={() => {
            setDateFrom(baghdadDaysAgo(30)); setDateTo(today);
          }}
          className="text-[10px] font-bold px-3 py-2 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-700"
        >
          آخر شهر
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm bg-white rounded-2xl border border-slate-100">
          لا توجد حزم بهذه الفترة — جرّب توسيع نطاق التاريخ.
        </div>
      ) : (
        bundles.map((b) => (
          <button key={b.id} type="button" onClick={() => onOpen(b)}
            data-testid={`bundle-card-${b.id}`}
            className="w-full text-right bg-white rounded-2xl border-2 border-purple-100 hover:border-purple-400 hover:shadow-lg p-3 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.blue})` }}>
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-base text-slate-800 truncate">🚚 {b.driver_name}</p>
                <p className="text-[10px] text-slate-500">{fmtDT(b.created_at)}</p>
              </div>
              <ChevronLeft className="w-5 h-5 text-slate-400 shrink-0" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="rounded-xl p-2" style={{ background: `${C.purple}0d` }}>
                <p className="text-[9px]" style={{ color: C.purple }}>عدد الفواتير</p>
                <p className="font-black text-sm" style={{ color: C.purple }}>{b.entries?.length || 0}</p>
              </div>
              <div className="rounded-xl p-2" style={{ background: `${C.green}0d` }}>
                <p className="text-[9px]" style={{ color: C.green }}>إجمالي الفواتير</p>
                <p className="font-black text-sm" dir="ltr" style={{ color: C.green }}>{fmt(b.total_invoiced)}</p>
              </div>
              <div className="rounded-xl p-2" style={{ background: `${C.red}0d` }}>
                <p className="text-[9px]" style={{ color: C.red }}>مديونية سابقة</p>
                <p className="font-black text-sm" dir="ltr" style={{ color: C.red }}>{fmt(b.total_previous_debt)}</p>
              </div>
            </div>
          </button>
        ))
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Bundle Detail — the actual "slip" the driver gets
// ═══════════════════════════════════════════════════════════════════════
const BundleDetail = ({ bundle: initialBundle, onBack, onDeleted }) => {
  const [bundle, setBundle] = useState(initialBundle);
  const [pickerOpen, setPickerOpen] = useState(false);
  const entries = bundle.entries || [];
  const grandTotal = Number(bundle.grand_total || 0);
  const totalInv = Number(bundle.total_invoiced || 0);
  const totalPrev = Number(bundle.total_previous_debt || 0);
  const totalCollected = entries.reduce((s, e) => s + Number(e.collected_amount || 0), 0);

  // Per-row inline input state (payment amount + optional note)
  const [drafts, setDrafts] = useState({}); // { [order_id]: { amount, note } }
  const [busyRow, setBusyRow] = useState(null);
  // After a successful collect, pop a receipt modal so the admin can
  // send the customer a nicely-formatted WhatsApp confirmation. Kept
  // in local state (not a route) to keep the flow inline & undoable.
  const [receipt, setReceipt] = useState(null); // { entry, amount, receiptNo, remaining }
  const setDraft = (oid, patch) => setDrafts((d) => ({ ...d, [oid]: { ...(d[oid] || {}), ...patch } }));

  const submitCollect = async (entry, isCashFull) => {
    const draft = drafts[entry.order_id] || {};
    const amount = isCashFull
      ? Number(entry.invoice_total || 0)
      : Number(draft.amount || 0);
    if (!(amount > 0)) { toast.error("اكتب مبلغ التسديد"); return; }
    setBusyRow(entry.order_id);
    try {
      const { data } = await axios.post(`${API}/admin/driver-bundles/${bundle.id}/collect`, {
        order_id: entry.order_id,
        amount,
        is_cash_full: !!isCashFull,
        note: (draft.note || "").trim(),
      });
      setBundle(data.bundle);
      setDrafts((d) => { const n = { ...d }; delete n[entry.order_id]; return n; });
      toast.success(`تم تسجيل سند قبض #${data.receipt_no}`);
      // Pull the fresh entry from the updated bundle so remaining-debt
      // reflects the just-saved payment (server is source of truth).
      const freshEntry = (data.bundle.entries || []).find((x) => x.order_id === entry.order_id) || entry;
      const invoiceTotal = Number(freshEntry.invoice_total || 0);
      const prevDebt = Number(freshEntry.previous_debt || 0);
      const collected = Number(freshEntry.collected_amount || 0);
      const remaining = Math.max(0, invoiceTotal + prevDebt - collected);
      setReceipt({
        entry: freshEntry,
        amount,
        receiptNo: data.receipt_no,
        remaining,
        isCashFull: !!isCashFull,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التسديد");
    } finally { setBusyRow(null); }
  };
  const buildText = () => {
    const header = `🚚 حزمة السائق: ${bundle.driver_name}\n📅 ${fmtDT(bundle.created_at)}\n─────────────`;
    const body = entries.map((e, i) => {
      const status = e.collection_status ? ` [${e.collection_label || "مسدد"}]` : " [آجل]";
      const lines = [`${i + 1}. ${e.customer_name}${status}`];
      if (e.customer_market_name) lines.push(`   🏪 ${e.customer_market_name}`);
      if (e.customer_address) lines.push(`   📍 ${e.customer_address}`);
      if (e.customer_phone) lines.push(`   📞 ${e.customer_phone}`);
      lines.push(`   • فاتورة: ${fmt(e.invoice_total)} د.ع`);
      lines.push(`   • سابقة: ${fmt(e.previous_debt)} د.ع`);
      if (e.collected_amount) lines.push(`   • مسدد: ${fmt(e.collected_amount)} د.ع`);
      return lines.join("\n");
    }).join("\n\n");
    const footer = `─────────────\n💰 إجمالي الفواتير: ${fmt(totalInv)} د.ع\n📌 مديونية سابقة: ${fmt(totalPrev)} د.ع\n✅ إجمالي المسدد: ${fmt(totalCollected)} د.ع\n💵 المجموع الكلي: ${fmt(grandTotal)} د.ع`;
    return `${header}\n${body}\n${footer}`;
  };
  const openWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(buildText())}`, "_blank");
  const doPrint = () => {
    const esc = (s) => String(s ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rows = entries.map((e, i) => {
      const cashLabel = e.collection_status ? (e.collection_label || "نقدا") : "آجل";
      const cashColor = e.collection_status === "cash_full" ? C.green : (e.collection_status ? C.orange : C.red);
      const infoParts = [];
      if (e.customer_market_name) infoParts.push(`<span class=badge>🏪 ${esc(e.customer_market_name)}</span>`);
      if (e.customer_address) infoParts.push(`<span class=addr>📍 ${esc(e.customer_address)}</span>`);
      if (e.customer_phone) infoParts.push(`<span class=phone dir=ltr>📞 ${esc(e.customer_phone)}</span>`);
      const info = infoParts.join(" ");
      return `<tr>
        <td class=num>${i + 1}</td>
        <td class=cust>
          <div class=cname>${esc(e.customer_name)}</div>
          ${info ? `<div class=cinfo>${info}</div>` : ""}
          <div class=cinv>فاتورة #${esc(e.invoice_number || "")}</div>
        </td>
        <td class=amt>${fmt(e.invoice_total)}</td>
        <td class=debt>${fmt(e.previous_debt)}</td>
        <td class=tot>${fmt(e.invoice_total + e.previous_debt)}</td>
        <td style="color:${cashColor};font-weight:900">${cashLabel}</td>
        <td class=notes>${esc(e.collection_note || "")}</td>
      </tr>`;
    }).join("");
    const html = `<!doctype html><html dir=rtl><head><meta charset=utf-8><title>حزمة ${esc(bundle.driver_name)}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:'Segoe UI',Tahoma,Arial;padding:20px;color:#1e293b;background:white}
        h1{background:linear-gradient(135deg,${C.purple},${C.blue});color:white;text-align:center;font-size:22px;margin:0;padding:14px;border-radius:12px}
        .sub{text-align:center;color:#64748b;font-size:12px;margin:10px 0 16px}
        .driver-info{background:#f8fafc;border-right:4px solid ${C.purple};padding:10px 14px;margin-bottom:14px;border-radius:8px}
        .driver-info b{color:${C.purple};font-size:15px}
        table{width:100%;border-collapse:collapse;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,.06);border-radius:10px;overflow:hidden}
        th{background:linear-gradient(135deg,${C.purple},${C.blue});color:white;padding:10px 6px;text-align:right;font-size:11px}
        td{padding:10px 6px;border-bottom:1px solid #e2e8f0;vertical-align:middle}
        tr:nth-child(even) td{background:#fafbfc}
        .num{text-align:center;background:${C.purple}15;color:${C.purple};font-weight:900;width:34px;font-size:14px}
        .cust{min-width:210px}
        .cname{font-weight:900;font-size:14px;color:#1e293b}
        .cinfo{margin-top:3px;display:flex;flex-wrap:wrap;gap:4px}
        .badge{background:${C.blue}15;color:${C.blue};padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700}
        .addr{color:#475569;font-size:11px}
        .phone{color:#64748b;font-size:11px}
        .cinv{color:#94a3b8;font-size:10px;margin-top:2px}
        .amt{color:${C.green};font-weight:900;text-align:center;direction:ltr;font-size:13px}
        .debt{color:${C.red};font-weight:900;text-align:center;direction:ltr;font-size:13px}
        .tot{color:${C.darkBlue};font-weight:900;text-align:center;direction:ltr;font-size:14px}
        .notes{color:#64748b;font-size:11px}
        tfoot td{background:#f1f5f9;font-weight:900;padding:12px 6px;font-size:13px}
        tfoot .lbl{text-align:right;color:#475569}
        .grand td{background:linear-gradient(135deg,${C.purple}25,${C.blue}25);color:${C.purple};font-size:16px;padding:14px 6px}
        @media print{body{padding:8px}}
      </style></head><body>
      <h1>🚚 حزمة توزيع — ${esc(bundle.driver_name)}</h1>
      <p class=sub>${fmtDT(bundle.created_at)} · عدد الزبائن: ${entries.length}</p>
      <div class=driver-info><b>السائق:</b> ${esc(bundle.driver_name)}</div>
      <table>
        <thead><tr><th>#</th><th>معلومات الزبون</th><th>الفاتورة</th><th>سابقة</th><th>الإجمالي</th><th>الحالة</th><th>ملاحظات</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr><td colspan=2 class=lbl>💰 إجمالي الفواتير</td><td class=amt>${fmt(totalInv)}</td><td></td><td></td><td></td><td></td></tr>
          <tr><td colspan=2 class=lbl>📌 مديونية سابقة</td><td></td><td class=debt>${fmt(totalPrev)}</td><td></td><td></td><td></td></tr>
          <tr><td colspan=2 class=lbl>✅ إجمالي المسدد</td><td colspan=2 class=amt>${fmt(totalCollected)}</td><td></td><td></td><td></td></tr>
          <tr class=grand><td colspan=4>💵 المجموع الكلي المطلوب من السائق</td><td class=tot>${fmt(grandTotal)}</td><td colspan=2></td></tr>
        </tfoot>
      </table>
      </body></html>`;
    printHTML(html, { title: `حزمة ${bundle.driver_name}` });
  };
  const doPdf = () => doPrint();
  const doDelete = async () => {
    if (!window.confirm(`حذف حزمة السائق «${bundle.driver_name}»؟ الفواتير رح ترجع لقائمة الفواتير المتاحة.`)) return;
    try {
      await axios.delete(`${API}/admin/driver-bundles/${bundle.id}`);
      toast.success("تم الحذف");
      onDeleted();
    } catch { toast.error("فشل الحذف"); }
  };

  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} data-testid="bundle-back"
        className="flex items-center gap-1.5 text-sm font-bold text-purple-700">
        <ArrowLeft className="w-4 h-4" /> رجوع
      </button>

      {/* Slip header — big beautiful orange→blue gradient with driver info */}
      <div className="rounded-3xl overflow-hidden shadow-xl border-2" style={{ borderColor: `${C.blue}22` }}>
        <div className="p-5 text-white text-center relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${C.orange} 0%, #f97316 40%, ${C.blue} 100%)` }}>
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/5" />
          <div className="relative z-10">
            <div className="w-20 h-20 rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg border-4 border-white/30"
              style={{ background: "linear-gradient(135deg, #ffffff30, #ffffff10)", backdropFilter: "blur(8px)" }}>
              <Truck className="w-10 h-10" strokeWidth={2.5} />
            </div>
            <p className="font-black text-2xl">{bundle.driver_name}</p>
            <div className="flex items-center justify-center gap-3 mt-2 text-[11px] opacity-90">
              <span>📅 {fmtDT(bundle.created_at)}</span>
              <span>·</span>
              <span>{entries.length} زبون</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 p-2 bg-white">
          <button type="button" onClick={() => setPickerOpen(true)} data-testid="bundle-add-customer"
            className="py-2.5 rounded-xl bg-orange-100 hover:bg-orange-200 text-orange-800 text-sm font-bold flex items-center justify-center gap-1.5 transition-all">
            <UserPlus className="w-4 h-4" /> إضافة زبون
          </button>
          <button type="button" onClick={openWhatsapp} data-testid="bundle-wa"
            className="py-2.5 rounded-xl bg-green-100 hover:bg-green-200 text-green-800 text-sm font-bold flex items-center justify-center gap-1.5 transition-all">
            <Share2 className="w-4 h-4" /> واتساب
          </button>
          <button type="button" onClick={doPdf} data-testid="bundle-pdf"
            className="py-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-800 text-sm font-bold flex items-center justify-center gap-1.5 transition-all">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button type="button" onClick={doPrint} data-testid="bundle-print"
            className="py-2.5 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm font-bold flex items-center justify-center gap-1.5 transition-all">
            <Printer className="w-4 h-4" /> طباعة
          </button>
        </div>
      </div>

      {pickerOpen && (
        <AddCustomerPicker
          bundle={bundle}
          onClose={() => setPickerOpen(false)}
          onAdded={(fresh) => { setBundle(fresh); setPickerOpen(false); }}
        />
      )}

      {receipt && (
        <ReceiptWhatsappModal
          receipt={receipt}
          onClose={() => setReceipt(null)}
        />
      )}

      {/* Slip body — one card per customer with inline payment controls */}
      <div className="space-y-3">
        {entries.map((e, i) => {
          const collected = Number(e.collected_amount || 0);
          const isCashFull = e.collection_status === "cash_full";
          const isPartial = e.collection_status === "partial";
          const isSettled = !!e.collection_status;
          const draft = drafts[e.order_id] || {};
          // Orange when unpaid (needs collection), green if fully paid,
          // amber if partial. Blue accents everywhere for the base theme.
          const accent = isCashFull ? C.green : (isPartial ? C.orange : C.orange);
          return (
            <div key={e.order_id}
              data-testid={`bundle-entry-${e.order_id}`}
              className="bg-white rounded-2xl overflow-hidden shadow-md border-2"
              style={{ borderColor: isCashFull ? `${C.green}55` : (isPartial ? `${C.orange}55` : `${C.blue}22`) }}>

              {/* HEADER — big orange→blue gradient with customer name */}
              <div className="p-3 relative overflow-hidden"
                style={{ background: `linear-gradient(120deg, ${C.orange}, ${C.blue})` }}>
                <div className="flex items-center gap-3 text-white relative z-10">
                  <div className="w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center font-black text-lg border-2 border-white/40 shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-lg leading-tight truncate">{e.customer_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {e.invoice_number && (
                        <span className="text-[11px] font-bold bg-white/20 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Hash className="w-3 h-3" /> {e.invoice_number}
                        </span>
                      )}
                      {isSettled ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-black flex items-center gap-1"
                          style={{ background: isCashFull ? "#ffffff" : "#fef3c7", color: isCashFull ? C.green : C.orange }}>
                          <Check className="w-3 h-3" strokeWidth={3} /> {e.collection_label}
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white text-red-600 font-black">آجل</span>
                      )}
                    </div>
                  </div>
                  {/* Remove-from-bundle basket (only when not settled) */}
                  {!isSettled && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`إزالة «${e.customer_name}» من هذي الحزمة؟ الفاتورة ما راح تنحذف — بس تطلع من قائمة السائق.`)) return;
                        try {
                          const { data } = await axios.delete(`${API}/admin/driver-bundles/${bundle.id}/entries/${e.order_id}`);
                          toast.success("تم الإزالة من الحزمة");
                          if (data.bundle_deleted) {
                            onDeleted && onDeleted();
                          } else if (data.bundle) {
                            setBundle(data.bundle);
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "فشل الحذف");
                        }
                      }}
                      data-testid={`bundle-remove-entry-${e.order_id}`}
                      title="إزالة من الحزمة"
                      className="shrink-0 w-9 h-9 rounded-xl bg-white/20 hover:bg-white/40 backdrop-blur flex items-center justify-center transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </div>

              {/* Contact info strip — market, address, phone */}
              <div className="px-3 py-2.5 space-y-1.5 bg-gradient-to-l from-blue-50/50 to-orange-50/30 border-b border-slate-100">
                {e.customer_market_name && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${C.blue}18` }}>
                      <Store className="w-3.5 h-3.5" style={{ color: C.blue }} />
                    </div>
                    <p className="text-sm font-bold truncate">{e.customer_market_name}</p>
                  </div>
                )}
                {e.customer_address && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${C.orange}18` }}>
                      <MapPin className="w-3.5 h-3.5" style={{ color: C.orange }} />
                    </div>
                    <p className="text-sm font-semibold truncate">{e.customer_address}</p>
                  </div>
                )}
                {e.customer_phone && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${C.green}18` }}>
                      <Phone className="w-3.5 h-3.5" style={{ color: C.green }} />
                    </div>
                    <a href={`tel:${e.customer_phone}`} className="text-sm font-bold hover:underline" dir="ltr" style={{ color: C.green }}>
                      {e.customer_phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Amounts row — bigger numbers, colored tiles */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-white">
                <div className="rounded-xl p-2 text-center border-2" style={{ background: `${C.blue}0d`, borderColor: `${C.blue}30` }}>
                  <p className="text-[10px] font-bold" style={{ color: C.blue }}>فاتورة اليوم</p>
                  <p className="font-black text-base mt-0.5" dir="ltr" style={{ color: C.blue }}>{fmt(e.invoice_total)}</p>
                </div>
                <div className="rounded-xl p-2 text-center border-2"
                  style={{ background: e.previous_debt > 0 ? `${C.red}0d` : "#f8fafc",
                           borderColor: e.previous_debt > 0 ? `${C.red}30` : "#e2e8f0" }}>
                  <p className="text-[10px] font-bold" style={{ color: e.previous_debt > 0 ? C.red : "#94a3b8" }}>دين سابق</p>
                  <p className="font-black text-base mt-0.5" dir="ltr" style={{ color: e.previous_debt > 0 ? C.red : "#94a3b8" }}>
                    {fmt(e.previous_debt)}
                  </p>
                </div>
                <div className="rounded-xl p-2 text-center border-2"
                  style={{ background: `${C.orange}0d`, borderColor: `${C.orange}40` }}>
                  <p className="text-[10px] font-bold" style={{ color: C.orange }}>الإجمالي</p>
                  <p className="font-black text-lg mt-0.5" dir="ltr" style={{ color: C.orange }}>
                    {fmt(e.invoice_total + e.previous_debt)}
                  </p>
                </div>
              </div>

              {/* Row 3 — inline payment controls (hidden once fully paid) */}
              {!isCashFull && (
                <div className="p-3 pt-0 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      value={draft.amount ?? ""}
                      onChange={(ev) => setDraft(e.order_id, { amount: ev.target.value })}
                      placeholder="مبلغ التسديد..."
                      data-testid={`bundle-pay-amount-${e.order_id}`}
                      className="flex-1 px-3 py-2.5 rounded-xl border-2 border-blue-100 text-base text-center font-black focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={() => submitCollect(e, false)}
                      disabled={busyRow === e.order_id || !(Number(draft.amount) > 0)}
                      data-testid={`bundle-pay-partial-${e.order_id}`}
                      className="px-4 py-2.5 rounded-xl text-white text-sm font-black shadow-md hover:shadow-lg disabled:opacity-40 flex items-center gap-1.5 transition-all"
                      style={{ background: `linear-gradient(135deg, ${C.orange}, #ea580c)` }}
                    >
                      <Banknote className="w-4 h-4" /> تسديد
                    </button>
                    <button
                      type="button"
                      onClick={() => submitCollect(e, true)}
                      disabled={busyRow === e.order_id}
                      data-testid={`bundle-pay-full-${e.order_id}`}
                      title="دفع نقدي كامل لفاتورة اليوم"
                      className="w-11 h-11 rounded-xl text-white flex items-center justify-center shadow-md hover:shadow-lg disabled:opacity-40 transition-all"
                      style={{ background: `linear-gradient(135deg, ${C.green}, #047857)` }}
                    >
                      <Check className="w-5 h-5" strokeWidth={3} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={draft.note ?? ""}
                    onChange={(ev) => setDraft(e.order_id, { note: ev.target.value })}
                    placeholder="ملاحظة (اختياري): عاجل، نقدا..."
                    data-testid={`bundle-pay-note-${e.order_id}`}
                    className="w-full px-3 py-2 rounded-xl border-2 border-slate-100 text-sm focus:outline-none focus:border-slate-400"
                  />
                </div>
              )}

              {/* Row 4 — already-collected badge + undo */}
              {collected > 0 && (
                <div className="mx-3 mb-3 rounded-xl p-3 flex items-center justify-between gap-2 border-2"
                  style={{ background: `${C.green}0d`, borderColor: `${C.green}30` }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.green }}>
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold" style={{ color: C.green }}>مسدد</p>
                      <p className="font-black text-base" dir="ltr" style={{ color: C.green }}>{fmt(collected)} د.ع</p>
                      {e.collection_note && <p className="text-[10px] text-slate-500 truncate">📝 {e.collection_note}</p>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`تراجع عن سند القبض #${e.collection_receipt_no || "—"}؟ الدين رح يرجع للزبون.`)) return;
                      try {
                        const { data } = await axios.post(`${API}/admin/driver-bundles/${bundle.id}/uncollect`, { order_id: e.order_id });
                        setBundle(data.bundle);
                        toast.success("تم التراجع وإرجاع الدين");
                      } catch (err) { toast.error(err.response?.data?.detail || "فشل التراجع"); }
                    }}
                    data-testid={`bundle-uncollect-${e.order_id}`}
                    className="text-xs px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-bold flex items-center gap-1.5 shrink-0 border-2 border-red-200 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> تراجع
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals — orange/blue theme */}
      <div className="rounded-3xl p-4 shadow-lg border-2 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${C.blue}0d, ${C.orange}0d)`, borderColor: `${C.orange}40` }}>
        <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full opacity-10" style={{ background: C.orange }} />
        <div className="relative z-10 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <Receipt className="w-4 h-4" style={{ color: C.blue }} />
              إجمالي الفواتير
            </span>
            <span className="font-black text-base" dir="ltr" style={{ color: C.blue }}>{fmt(totalInv)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <span className="text-red-500">📌</span>
              مديونية سابقة
            </span>
            <span className="font-black text-base" dir="ltr" style={{ color: C.red }}>{fmt(totalPrev)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <Check className="w-4 h-4" style={{ color: C.green }} />
              إجمالي المسدد
            </span>
            <span className="font-black text-base" dir="ltr" style={{ color: C.green }}>{fmt(totalCollected)}</span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t-2 border-dashed" style={{ borderColor: `${C.orange}40` }}>
          <div className="flex items-center justify-between">
            <span className="font-black text-base flex items-center gap-2" style={{ color: C.orange }}>
              <Banknote className="w-5 h-5" /> المطلوب من السائق
            </span>
            <span className="font-black text-2xl" dir="ltr" style={{ color: C.orange }}>{fmt(grandTotal)}</span>
          </div>
        </div>
      </div>

      <button type="button" onClick={doDelete} data-testid="bundle-delete"
        className="w-full py-2.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100 font-bold text-sm flex items-center justify-center gap-1.5">
        <Trash2 className="w-4 h-4" /> حذف الحزمة
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Materials Picker — 4th tab: aggregated materials per driver bundle
// ═══════════════════════════════════════════════════════════════════════
const BundleMaterialsPicker = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [matLoading, setMatLoading] = useState(false);

  const imgSrc = (obj) => {
    if (!obj) return "";
    if (obj.image_url) return obj.image_url;
    if (obj.image_path) return `${API}/files/${obj.image_path}`;
    return "";
  };

  useEffect(() => {
    (async () => {
      try {
        const today = baghdadToday();
        const firstOfMonth = baghdadFirstOfMonth();
        const { data } = await axios.get(`${API}/admin/driver-bundles`, {
          params: { date_from: firstOfMonth, date_to: today },
        });
        setBundles(data.bundles || []);
      } catch { toast.error("فشل تحميل الحزم"); }
      finally { setLoading(false); }
    })();
  }, []);

  const openBundle = async (b) => {
    setSelectedBundle(b);
    setMatLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/driver-bundles/${b.id}/materials`);
      setMaterials(data);
    } catch { toast.error("فشل تحميل المواد"); }
    finally { setMatLoading(false); }
  };

  const buildText = () => {
    if (!materials) return "";
    const header = `📦 قائمة تحميل السائق: ${materials.driver_name}\n📅 ${fmtDT(materials.created_at)}\n🧾 ${materials.totals.orders} فاتورة · ${materials.totals.lines} صنف · ${materials.totals.cartons} كرتون\n─────────────`;
    const groups = (materials.by_category || []).map((g) => {
      const rows = g.items.map((it, i) => `${i + 1}. ${it.name_ar}${it.brand ? ` — ${it.brand}` : ""}\n   • ${it.qty} ${it.unit}`).join("\n");
      return `\n📂 ${g.category} (${g.total_qty} كرتون)\n${rows}`;
    }).join("\n");
    return `${header}${groups}\n─────────────\n📊 المجموع: ${materials.totals.cartons} كرتون`;
  };

  const openWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(buildText())}`, "_blank");

  const doPrint = () => {
    if (!materials) return;

    // Flatten items with category tag. NO images — keeps the printed
    // sheet fast to render and small (was crashing Chrome's print
    // preview when many products were included with base64 blobs).
    const flat = [];
    (materials.by_category || []).forEach((g) => {
      g.items.forEach((it) => flat.push({ ...it, category: g.category }));
    });

    const esc = (s) => String(s ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const CHUNK = 15;
    const chunks = [];
    for (let i = 0; i < flat.length; i += CHUNK) chunks.push(flat.slice(i, i + CHUNK));

    const chunkHtml = (rows, chunkIndex) => {
      const startIdx = chunkIndex * CHUNK;
      const body = rows.map((it, i) => {
        return `<tr>
          <td class=num>${startIdx + i + 1}</td>
          <td class=name>
            <div class=nm>${esc(it.name_ar)}</div>
            <div class=meta>
              <span class=cat>${esc(it.category || "—")}</span>
              ${it.brand ? `<span class=br>${esc(it.brand)}</span>` : ""}
              ${it.customer_count > 1 ? `<span class=cu>${it.customer_count} زبون</span>` : ""}
            </div>
          </td>
          <td class=qty>${it.qty}</td>
          <td class=unit>${esc(it.unit || "كرتون")}</td>
        </tr>`;
      }).join("");
      const chunkTotal = rows.reduce((s, it) => s + Number(it.qty || 0), 0);
      return `<div class=tbl>
        <table>
          <thead>
            <tr>
              <th class=num>#</th>
              <th class=name>اسم المادة</th>
              <th class=qty>الكمية</th>
              <th class=unit>الوحدة</th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
          <tfoot><tr><td colspan=2>مجموع الجدول</td><td class=qty>${chunkTotal}</td><td></td></tr></tfoot>
        </table>
      </div>`;
    };

    const pages = [];
    for (let i = 0; i < chunks.length; i += 4) {
      const pageChunks = chunks.slice(i, i + 4);
      pages.push(`<div class=page>
        <div class=grid>
          ${pageChunks.map((rows, ci) => chunkHtml(rows, i + ci)).join("")}
        </div>
      </div>`);
    }

    const html = `<!doctype html><html dir=rtl><head><meta charset=utf-8>
      <title>مواد ${esc(materials.driver_name)}</title>
      <style>
        *{box-sizing:border-box}
        @page{size:A4;margin:8mm}
        body{font-family:'Segoe UI',Tahoma,Arial;padding:0;margin:0;color:#1e293b;background:white;font-size:10px}
        h1{background:linear-gradient(135deg,${C.blue},${C.orange});color:white;text-align:center;font-size:16px;margin:0 0 6px;padding:8px;border-radius:8px}
        .sub{text-align:center;color:#64748b;font-size:10px;margin:0 0 6px}
        .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin:0 0 8px}
        .stat{background:#f8fafc;border-right:3px solid ${C.blue};padding:5px 8px;border-radius:5px;text-align:center}
        .stat b{display:block;font-size:14px;color:${C.blue};line-height:1.1}
        .stat span{font-size:8px;color:#64748b}
        .page{page-break-after:always}
        .page:last-child{page-break-after:auto}
        .grid{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto auto;gap:6px;align-items:start}
        .tbl{page-break-inside:avoid}
        table{width:100%;border-collapse:collapse;font-size:9px;border:1.5px solid ${C.blue}55;border-radius:5px;overflow:hidden}
        th{background:linear-gradient(135deg,${C.blue},${C.blue}dd);color:white;padding:4px 3px;text-align:right;font-size:9px;font-weight:900}
        td{padding:3px;border-bottom:1px solid #e2e8f0;vertical-align:middle}
        tr:nth-child(even) td{background:#fafbfc}
        th.num,td.num{text-align:center;background:${C.blue}12;color:${C.blue};font-weight:900;width:26px;font-size:11px}
        th.name,td.name{min-width:110px}
        .nm{font-weight:900;line-height:1.2;font-size:10px}
        .meta{margin-top:1px;display:flex;flex-wrap:wrap;gap:2px}
        .meta span{font-size:7px;padding:1px 3px;border-radius:2px;font-weight:700}
        .cat{background:${C.blue}18;color:${C.blue}}
        .br{background:#f1f5f9;color:#64748b}
        .cu{background:${C.orange}20;color:${C.orange}}
        th.qty,td.qty{text-align:center;font-size:13px;font-weight:900;color:${C.orange};width:36px}
        th.unit,td.unit{text-align:center;color:#64748b;width:36px;font-size:8px}
        tfoot td{background:${C.orange}10;color:${C.orange};font-weight:900;font-size:9px}
        tfoot td.qty{font-size:12px}
        @media print{
          .page{page-break-after:always}
          body{padding:0}
        }
      </style></head><body>
      <h1>📦 قائمة تحميل — ${esc(materials.driver_name)}</h1>
      <p class=sub>${fmtDT(materials.created_at)}</p>
      <div class=stats>
        <div class=stat><b>${materials.totals.orders}</b><span>فاتورة</span></div>
        <div class=stat><b>${materials.totals.lines}</b><span>صنف</span></div>
        <div class=stat><b>${materials.totals.cartons}</b><span>كرتون</span></div>
      </div>
      <div id="printBar" style="position:sticky;top:0;background:white;padding:8px;text-align:center;z-index:100;border-bottom:2px solid #e5e7eb;margin:0 0 10px" class="no-print">
        <button onclick="window.print()" style="background:linear-gradient(135deg,${C.blue},${C.orange});color:white;border:none;padding:10px 30px;border-radius:8px;font-weight:900;font-size:14px;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.15)">
          🖨️ طباعة الآن
        </button>
        <span style="margin-right:12px;color:#64748b;font-size:12px">أو اضغط Ctrl+P</span>
      </div>
      <style>@media print{.no-print{display:none!important}}</style>
      ${pages.join("")}
      </body></html>`;
    printHTML(html, { title: `قائمة تحميل — ${materials.driver_name}` });
  };

  if (selectedBundle) {
    return (
      <div className="space-y-3">
        <button onClick={() => { setSelectedBundle(null); setMaterials(null); }}
          data-testid="materials-back"
          className="flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm font-bold">
          <ArrowLeft className="w-4 h-4" /> رجوع للحزم
        </button>
        {matLoading || !materials ? (
          <div className="text-center py-10 text-slate-400">جاري التحميل...</div>
        ) : (
          <>
            {/* Header */}
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <div className="p-4 text-white relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${C.blue}, ${C.orange})` }}>
                <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
                <div className="relative z-10 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                    <Package className="w-7 h-7" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs opacity-90">قائمة التحميل</p>
                    <h3 className="font-black text-xl">{materials.driver_name}</h3>
                    <p className="text-[11px] opacity-90 mt-0.5">{fmtDT(materials.created_at)}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 p-3 bg-white">
                <div className="rounded-xl p-2 text-center border-2 border-blue-200 bg-blue-50">
                  <p className="text-[10px] font-bold text-blue-700">فواتير</p>
                  <p className="font-black text-xl text-blue-700">{materials.totals.orders}</p>
                </div>
                <div className="rounded-xl p-2 text-center border-2 border-slate-200 bg-slate-50">
                  <p className="text-[10px] font-bold text-slate-700">أصناف</p>
                  <p className="font-black text-xl text-slate-700">{materials.totals.lines}</p>
                </div>
                <div className="rounded-xl p-2 text-center border-2 border-orange-200 bg-orange-50">
                  <p className="text-[10px] font-bold text-orange-700">كرتون</p>
                  <p className="font-black text-xl text-orange-700">{materials.totals.cartons}</p>
                </div>
              </div>
            </div>

            {/* Export actions */}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={openWhatsapp} data-testid="materials-wa"
                className="py-2.5 rounded-xl bg-green-100 hover:bg-green-200 text-green-800 text-sm font-bold flex items-center justify-center gap-1.5">
                <Share2 className="w-4 h-4" /> واتساب
              </button>
              <button onClick={doPrint} data-testid="materials-pdf"
                className="py-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-800 text-sm font-bold flex items-center justify-center gap-1.5">
                <Download className="w-4 h-4" /> PDF
              </button>
              <button onClick={doPrint} data-testid="materials-print"
                className="py-2.5 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm font-bold flex items-center justify-center gap-1.5">
                <Printer className="w-4 h-4" /> طباعة
              </button>
            </div>

            {/* Grouped tables per category */}
            {(materials.by_category || []).map((g, gi) => (
              <div key={g.category + gi} className="bg-white rounded-2xl overflow-hidden shadow-md border-2"
                style={{ borderColor: `${C.blue}22` }}>
                <div className="px-3 py-2 flex items-center justify-between"
                  style={{ background: `linear-gradient(90deg, ${C.orange}15, ${C.blue}15)` }}>
                  <h4 className="font-black text-sm flex items-center gap-1.5" style={{ color: C.blue }}>
                    📂 {g.category}
                  </h4>
                  <span className="text-xs font-black" style={{ color: C.orange }}>{g.total_qty} كرتون</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {g.items.map((it, i) => (
                    <div key={(it.product_id || it.name_ar) + i}
                      data-testid={`material-row-${it.product_id || i}`}
                      className="p-3 flex items-center gap-3 hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 text-white"
                        style={{ background: C.blue }}>
                        {i + 1}
                      </div>
                      {imgSrc(it) ? (
                        <img src={imgSrc(it)} alt={it.name_ar}
                          loading="lazy" decoding="async"
                          className="w-12 h-12 object-cover rounded-lg border-2 border-slate-100 shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          <Package className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-black text-sm text-slate-800 truncate">{it.name_ar}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {it.brand && <span className="text-[10px] text-blue-600 font-bold">{it.brand}</span>}
                          {it.customer_count > 1 && (
                            <span className="text-[10px] text-orange-600 font-bold">👥 {it.customer_count} زبون</span>
                          )}
                        </div>
                      </div>
                      <div className="text-left shrink-0">
                        <p className="font-black text-2xl" style={{ color: C.orange }}>{it.qty}</p>
                        <p className="text-[9px] text-slate-500 text-center">{it.unit || "كرتون"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-3 text-center">
        <Package className="w-8 h-8 mx-auto mb-1" style={{ color: C.blue }} />
        <p className="text-sm font-bold text-slate-700">اختر حزمة سائق لعرض قائمة التحميل</p>
        <p className="text-[11px] text-slate-500 mt-1">
          سنجمع كل المواد من فواتير هذه الحزمة في قائمة واحدة، حتى لو تكرّرت المادة عند أكثر من زبون.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">جاري التحميل...</div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">لا توجد حزم بهذا الشهر</div>
      ) : (
        <div className="space-y-2">
          {bundles.map((b) => (
            <button key={b.id} type="button" onClick={() => openBundle(b)}
              data-testid={`materials-bundle-${b.id}`}
              className="w-full bg-white rounded-2xl border-2 border-blue-100 hover:border-blue-400 p-3 flex items-center gap-3 shadow-sm hover:shadow-md transition-all text-right">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: `linear-gradient(135deg, ${C.blue}, ${C.orange})` }}>
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm">{b.driver_name}</p>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                  <span>📅 {fmtDT(b.created_at)}</span>
                  <span>·</span>
                  <span>🧾 {(b.entries || []).length} فاتورة</span>
                </div>
              </div>
              <ChevronLeft className="w-5 h-5 text-slate-400 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Collections Report — driver-level totals + per-collection detail
// ═══════════════════════════════════════════════════════════════════════
const CollectionsReport = () => {
  const today = baghdadToday();
  const firstOfMonth = baghdadFirstOfMonth();
  const [driverName, setDriverName] = useState("");
  const [customerQ, setCustomerQ] = useState("");
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState({ collections: [], by_driver: [], total_collected: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [openDriver, setOpenDriver] = useState(null);
  const deferredCust = useDeferredValue(customerQ);
  const deferredDriver = useDeferredValue(driverName);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/driver-collections-report`, {
        params: { driver_name: deferredDriver, customer_q: deferredCust, date_from: dateFrom, date_to: dateTo },
      });
      setData(data);
    } catch { toast.error("فشل تحميل التقرير"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [deferredDriver, deferredCust, dateFrom, dateTo]);

  const shownCollections = useMemo(() => {
    if (!openDriver) return data.collections;
    return (data.collections || []).filter((c) => c.driver_name === openDriver);
  }, [data.collections, openDriver]);

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="bg-white rounded-2xl border-2 border-green-100 p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input type="text" value={driverName} onChange={(e) => setDriverName(e.target.value)}
              placeholder="اسم السائق..." data-testid="report-driver-name"
              className="w-full pr-8 pl-2 py-1.5 rounded-lg border-2 border-green-100 focus:outline-none focus:border-green-400 text-xs" />
          </div>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input type="text" value={customerQ} onChange={(e) => setCustomerQ(e.target.value)}
              placeholder="اسم الزبون..." data-testid="report-customer-q"
              className="w-full pr-8 pl-2 py-1.5 rounded-lg border-2 border-green-100 focus:outline-none focus:border-green-400 text-xs" />
          </div>
          <div>
            <label className="text-[9px] text-slate-500">من</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-2 py-1 rounded-lg border border-slate-200 text-xs" />
          </div>
          <div>
            <label className="text-[9px] text-slate-500">إلى</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-2 py-1 rounded-lg border border-slate-200 text-xs" />
          </div>
        </div>
      </div>

      {/* Grand summary */}
      <div className="rounded-2xl p-3 text-white text-center" style={{ background: `linear-gradient(135deg, ${C.green}, #10B981)` }}>
        <p className="text-[10px] opacity-90">إجمالي المحصَّل بالفترة ({data.total_count} تسديد)</p>
        <p className="font-black text-3xl" dir="ltr">{fmt(data.total_collected)} د.ع</p>
      </div>

      {/* By driver */}
      {loading ? (
        <div className="text-center py-6 text-slate-500">جاري التحميل...</div>
      ) : data.by_driver.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm bg-white rounded-2xl border border-slate-100">
          لا توجد تسديدات بهذه الفترة
        </div>
      ) : (
        <>
          <p className="text-xs font-black text-slate-600">📊 حسب السائق</p>
          <div className="space-y-2">
            {data.by_driver.map((d) => {
              const isOpen = openDriver === d.driver_name;
              return (
                <button key={d.driver_name} type="button"
                  onClick={() => setOpenDriver(isOpen ? null : d.driver_name)}
                  data-testid={`report-driver-${d.driver_name}`}
                  className="w-full text-right bg-white rounded-2xl border-2 p-3 hover:shadow-lg transition-all"
                  style={{ borderColor: isOpen ? C.green : "#e2e8f0" }}>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.blue})` }}>
                      <Truck className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-base text-slate-800 truncate">{d.driver_name}</p>
                      <p className="text-[10px] text-slate-500">{d.count} تسديد</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[9px]" style={{ color: C.green }}>نقدي</p>
                      <p className="font-black text-sm" dir="ltr" style={{ color: C.green }}>{fmt(d.cash_total)}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-[9px]" style={{ color: C.orange }}>جزئي</p>
                      <p className="font-black text-sm" dir="ltr" style={{ color: C.orange }}>{fmt(d.partial_total)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Collections list */}
          <p className="text-xs font-black text-slate-600 mt-3">
            📝 {openDriver ? `تسديدات «${openDriver}»` : "كل التسديدات"} ({shownCollections.length})
          </p>
          <div className="space-y-1.5">
            {shownCollections.map((c) => (
              <div key={`${c.bundle_id}-${c.order_id}`}
                data-testid={`report-row-${c.order_id}`}
                className="bg-white rounded-xl border p-2.5 flex items-center gap-2"
                style={{ borderColor: c.collection_status === "cash_full" ? `${C.green}55` : `${C.orange}55` }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
                  style={{ background: c.collection_status === "cash_full" ? C.green : C.orange }}>
                  <Banknote className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    <p className="font-black text-xs text-slate-800 truncate">{c.customer_name}</p>
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold"
                      style={{ background: c.collection_status === "cash_full" ? `${C.green}20` : `${C.orange}20`,
                               color: c.collection_status === "cash_full" ? C.green : C.orange }}>
                      {c.collection_label}
                    </span>
                    {c.receipt_no && <span className="text-[8px] text-blue-600">#{c.receipt_no}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-500">
                    <span>🚚 {c.driver_name}</span>
                    <span>{fmtDT(c.collected_at || c.bundle_date)}</span>
                  </div>
                  {c.collection_note && <p className="text-[10px] text-slate-500 mt-0.5 truncate">📝 {c.collection_note}</p>}
                </div>
                <div className="text-left shrink-0">
                  <p className="font-black text-sm" dir="ltr"
                    style={{ color: c.collection_status === "cash_full" ? C.green : C.orange }}>
                    {fmt(c.collected_amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DriverBundlesTab;

// ═══════════════════════════════════════════════════════════════════════
// Add Customer Picker — inside a BundleDetail, pop this modal to attach
// more confirmable orders to the current driver's bundle.
// ═══════════════════════════════════════════════════════════════════════
const AddCustomerPicker = ({ bundle, onClose, onAdded }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const dq = useDeferredValue(q);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/confirmable-orders`, {
          params: { include_hidden: false },
        });
        setRows(Array.isArray(data.orders) ? data.orders : []);
      } catch (e) {
        toast.error("فشل تحميل الفواتير");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = (dq || "").trim();
    if (!term) return rows;
    return rows.filter((o) =>
      smartMatch(term, o.user_name || "") ||
      smartMatch(term, o.market_name || "") ||
      smartMatch(term, o.address || "") ||
      (o.user_phone || "").includes(term) ||
      (o.invoice_number || "").toString().includes(term)
    );
  }, [rows, dq]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("اختر زبون واحد على الأقل"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/driver-bundles/${bundle.id}/entries`,
        { order_ids: Array.from(selected) },
      );
      toast.success(`تم إضافة ${data.added} زبون`);
      onAdded && onAdded(data.bundle);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإضافة");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3"
      dir="rtl"
      data-testid="bundle-add-customer-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.blue})` }}>
          <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -right-4 w-24 h-24 rounded-full bg-white/5" />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                <UserPlus className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs opacity-90">إضافة زبون لحزمة</p>
                <h3 className="font-black text-lg">{bundle.driver_name}</h3>
              </div>
            </div>
            <button onClick={onClose} data-testid="bundle-add-customer-close"
              className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-3 bg-slate-50 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="bundle-add-customer-search"
              placeholder="ابحث باسم الزبون / السوق / الفاتورة / الهاتف"
              className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm focus:outline-none text-slate-900 placeholder-slate-400"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            {selected.size > 0 ? `تم اختيار ${selected.size} فاتورة` : `${filtered.length} فاتورة متاحة`}
          </p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
          {loading ? (
            <div className="text-center py-10">
              <Loader2 className="w-8 h-8 animate-spin inline text-orange-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="font-bold text-slate-500">لا توجد فواتير متاحة</p>
              <p className="text-xs">كل الفواتير المؤكدة معينة لحزم</p>
            </div>
          ) : (
            filtered.map((o) => {
              const isSel = selected.has(o.id);
              const total = Number(o.total || 0) + Number(o.previous_debt || 0);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  data-testid={`bundle-add-customer-row-${o.id}`}
                  className={`w-full text-right rounded-2xl p-3 border-2 shadow-sm transition-all flex items-center gap-3 ${isSel ? "bg-orange-50 border-orange-400" : "bg-white border-slate-200 hover:border-orange-200"}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isSel ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {isSel ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-black text-sm text-slate-800 truncate">{o.user_name || "—"}</p>
                      {o.invoice_number && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center gap-0.5">
                          <Hash className="w-3 h-3" />{o.invoice_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 flex-wrap">
                      {o.market_name && <span className="truncate">🏪 {o.market_name}</span>}
                      {o.address && <span className="truncate">📍 {o.address}</span>}
                      {o.user_phone && <span dir="ltr">📞 {o.user_phone}</span>}
                    </div>
                  </div>
                  <div className="text-left shrink-0">
                    <p className="text-[10px] text-slate-500">فاتورة</p>
                    <p className="font-black text-sm text-emerald-700" dir="ltr">{fmt(o.total)}</p>
                    {Number(o.previous_debt || 0) > 0 && (
                      <p className="text-[10px] font-bold text-red-600" dir="ltr">سابقة: {fmt(o.previous_debt)}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-3 bg-white border-t border-slate-100 flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
          >
            إلغاء
          </button>
          <button
            onClick={submit}
            disabled={saving || selected.size === 0}
            data-testid="bundle-add-customer-save"
            className="flex-1 py-2.5 rounded-xl text-white font-black text-sm flex items-center justify-center gap-1.5 shadow-md disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.blue})` }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            إضافة للحزمة ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════════
// Receipt → WhatsApp modal — after a driver collect saves, pop this so
// the admin can (optionally) fire a nicely-formatted receipt to the
// customer's WhatsApp with a warm greeting + payment + remaining debt.
// ═══════════════════════════════════════════════════════════════════════
// Iraqi mobile numbers → WhatsApp E.164: drop leading 0, prepend 964.
// Any non-digits are stripped defensively.
const toWaPhone = (raw) => {
  const digits = (raw || "").toString().replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("964")) return digits;
  if (digits.startsWith("00964")) return digits.slice(2);
  if (digits.startsWith("0")) return "964" + digits.slice(1);
  return "964" + digits;
};

const ReceiptWhatsappModal = ({ receipt, onClose }) => {
  const { entry, amount, receiptNo, remaining, isCashFull } = receipt;
  const [customMsg, setCustomMsg] = useState("");
  const hasPhone = !!(entry.customer_phone || "").trim();
  const nowStr = new Date().toLocaleString("en-GB", { hour12: false });

  // Warm, short, brand-consistent receipt text. Uses distinct emoji per
  // line so it renders as a mini-invoice card inside WhatsApp.
  const buildReceiptText = () => {
    const name = (entry.customer_name || "الزبون الكريم").trim();
    const lines = [
      `مرحباً ${name} 👋`,
      `تسلم إيدك على الطلبية 🌹`,
      ``,
      `📝 سند قبض رقم: #${receiptNo}`,
      `📅 التاريخ: ${nowStr}`,
      `━━━━━━━━━━━━━━`,
      `💵 المبلغ المستلم: ${fmt(amount)} د.ع`,
      `${isCashFull ? "✅ الحالة: تسديد كامل نقدي" : "🟠 الحالة: تسديد جزئي"}`,
    ];
    if (remaining > 0) {
      lines.push(`📌 الباقي عليك: ${fmt(remaining)} د.ع`);
    } else {
      lines.push(`🎉 حسابك مسدد بالكامل — بارك الله بيك!`);
    }
    lines.push(`━━━━━━━━━━━━━━`);
    if (customMsg.trim()) {
      lines.push(customMsg.trim());
      lines.push("");
    }
    lines.push(`شكراً لثقتك بينا 💙`);
    lines.push(`— جملتي 🛒`);
    return lines.join("\n");
  };

  const send = () => {
    const text = encodeURIComponent(buildReceiptText());
    const phone = toWaPhone(entry.customer_phone);
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(url, "_blank");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[135] bg-black/60 flex items-center justify-center p-3"
      dir="rtl"
      data-testid="receipt-wa-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header — green success gradient */}
        <div className="p-4 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
          <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -right-4 w-24 h-24 rounded-full bg-white/5" />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                <Check className="w-6 h-6" strokeWidth={3} />
              </div>
              <div>
                <p className="text-xs opacity-90">تم التسديد بنجاح</p>
                <h3 className="font-black text-lg">سند قبض #{receiptNo}</h3>
              </div>
            </div>
            <button onClick={onClose} data-testid="receipt-wa-close"
              className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Payment summary */}
        <div className="p-4 bg-slate-50 space-y-3 overflow-y-auto">
          <div className="bg-white rounded-2xl p-3 border-2 border-emerald-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "linear-gradient(135deg, #F5A623, #3B82F6)" }}>
                <span className="text-white font-black text-lg">
                  {(entry.customer_name || "؟").slice(0, 1)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm text-slate-800 truncate">{entry.customer_name}</p>
                {entry.customer_phone && (
                  <p className="text-[11px] text-slate-500" dir="ltr">📞 {entry.customer_phone}</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl p-3 text-center border-2 bg-emerald-50 border-emerald-200">
              <p className="text-[10px] font-bold text-emerald-700">المبلغ المستلم</p>
              <p className="text-lg font-black text-emerald-700 mt-0.5" dir="ltr">{fmt(amount)}</p>
              <p className="text-[9px] text-emerald-600">دينار</p>
            </div>
            <div className="rounded-xl p-3 text-center border-2"
              style={{
                background: remaining > 0 ? "#fef2f2" : "#f0fdf4",
                borderColor: remaining > 0 ? "#fecaca" : "#bbf7d0",
              }}>
              <p className="text-[10px] font-bold" style={{ color: remaining > 0 ? "#dc2626" : "#16a34a" }}>
                الباقي على الزبون
              </p>
              <p className="text-lg font-black mt-0.5" dir="ltr"
                style={{ color: remaining > 0 ? "#dc2626" : "#16a34a" }}>
                {fmt(remaining)}
              </p>
              <p className="text-[9px]" style={{ color: remaining > 0 ? "#dc2626" : "#16a34a" }}>
                {remaining > 0 ? "دينار" : "مسدد بالكامل"}
              </p>
            </div>
          </div>

          {/* Optional custom line — admin can add "بارك الله بيك" etc. */}
          <div>
            <label className="text-[11px] font-black text-slate-600 mb-1 block">
              رسالة إضافية للزبون (اختياري)
            </label>
            <textarea
              value={customMsg}
              onChange={(e) => setCustomMsg(e.target.value)}
              data-testid="receipt-wa-custom-msg"
              rows={2}
              placeholder="مثال: بارك الله بيك، ننتظر طلبك القادم..."
              className="w-full px-3 py-2 rounded-xl border-2 border-emerald-100 focus:border-emerald-400 focus:outline-none text-sm bg-white resize-none text-slate-900 placeholder-slate-400"
            />
          </div>

          {!hasPhone && (
            <div className="rounded-xl p-2 bg-amber-50 border border-amber-200 text-[11px] text-amber-800 text-center">
              ⚠️ ما موجود رقم هاتف لهذا الزبون — راح ينفتح واتساب فارغ لتختار الرقم
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-3 bg-white border-t border-slate-100 grid grid-cols-2 gap-2">
          <button
            onClick={onClose}
            data-testid="receipt-wa-skip"
            className="py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
          >
            تخطي
          </button>
          <button
            onClick={send}
            data-testid="receipt-wa-send"
            className="py-3 rounded-xl text-white font-black text-sm flex items-center justify-center gap-1.5 shadow-md"
            style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)" }}
          >
            <Share2 className="w-4 h-4" /> إرسال واتساب
          </button>
        </div>
      </div>
    </div>
  );
};
